import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Translation } from '@ps/web-shared-components';
import {
  GERMANY_NAME_ALLOWED_CHARACTERS,
  GERMANY_NAME_VALID_CHARACTERS,
  NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
  mustContainLetter,
  mustContainOnlyLatinCharacters,
  mustNotContainNumber,
  mustNotContainSymbols,
  noConsecutiveHyphens,
  noConsecutiveSpaces,
  noWhiteStartOrEnd,
  required,
  validateAllowedCharacters
} from '@ps/web-shared-validators';
import React from 'react';
import type { FieldMetaState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import useAutoFocusInput from '../../../../../../hooks/useAutoFocusIf';
import TextInput from '../../../../../TextInput/TextInput';

import {
  TXTWCL_Add_a_single_hyphen_if_itXs_mi_ELL,
  TXTWCL_First_name,
  TXTWCL_First_nameXsXX,
  TXTWCL_Make_sure_to_add_your_full_nam_ELL,
  TXTWCL_Please_enter_your_first_nameX,
  TXTWCL_Please_enter_your_first_name_a_ELL,
  TXTWCL_The_name_you_have_entered_is_i_ELL
} from './GivenName.messages.json';

const fieldName = 'firstName';
const maxLength = 50;
const DE_MAX_LENGTH = 85;
const deValidators = [
  validateAllowedCharacters(GERMANY_NAME_ALLOWED_CHARACTERS, GERMANY_NAME_VALID_CHARACTERS),
  noConsecutiveSpaces,
  noConsecutiveHyphens
];
const grValidators = [
  mustContainOnlyLatinCharacters(),
  noConsecutiveSpaces,
  mustNotContainSymbols(
    NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
    TXTWCL_The_name_you_have_entered_is_i_ELL
  )
];

interface Props {
  autoFocus: boolean;
  meta?: FieldMetaState<string>;
}

const GivenName: React.FC<Props> = ({ autoFocus, meta }) => {
  const inputRef = useAutoFocusInput(autoFocus);
  const siteId = useSelector(select.cms.getSiteId);
  const isDe = siteId === Sites.DE;
  const isPT = siteId === Sites.POR;
  const isRO = siteId === Sites.ROM;
  const isGr = siteId === Sites.GR;
  const isEs = siteId === Sites.SPA;

  return (
    <TextInput
      id={fieldName}
      meta={{ ...meta, touched: true }}
      label={isPT || isEs ? TXTWCL_First_nameXsXX : TXTWCL_First_name}
      maxLength={isDe ? DE_MAX_LENGTH : maxLength}
      checkDisabled
      helperMessage={
        (isRO && <Translation {...TXTWCL_Please_enter_your_first_name_a_ELL} />) ||
        (isEs && <Translation {...TXTWCL_Make_sure_to_add_your_full_nam_ELL} />)
      }
      validators={[
        mustContainLetter(TXTWCL_The_name_you_have_entered_is_i_ELL),
        noWhiteStartOrEnd,
        required(
          isDe
            ? [TXTWCL_Please_enter_your_first_nameX, TXTWCL_Add_a_single_hyphen_if_itXs_mi_ELL]
            : TXTWCL_Please_enter_your_first_nameX
        ),
        mustNotContainNumber(TXTWCL_The_name_you_have_entered_is_i_ELL),
        ...(isDe
          ? deValidators
          : isGr
            ? grValidators
            : [
                mustNotContainSymbols(
                  NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
                  TXTWCL_The_name_you_have_entered_is_i_ELL
                )
              ])
      ]}
      inputRef={inputRef}
    />
  );
};

export default GivenName;
